import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Wand2, Users, Globe, Swords, Crosshair, BookOpen, Dices, Feather, Layers, ArrowRight, Bookmark, Eye, Bot, KeyRound, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import MatrixRain from '../components/MatrixRain';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RULESETS = [
  { id: 'dnd5e', name: 'D&D 5th Edition', icon: <Swords className="h-8 w-8" />, color: 'text-red-400', border: 'border-red-500/20 hover:border-red-400/50', glow: 'hover:shadow-red-500/10', desc: 'd20 system with advantage/disadvantage, spell slots, and iconic character classes' },
  { id: 'pathfinder2e', name: 'Pathfinder 2e', icon: <Crosshair className="h-8 w-8" />, color: 'text-amber-400', border: 'border-amber-500/20 hover:border-amber-400/50', glow: 'hover:shadow-amber-500/10', desc: 'Tactical 3-action economy with four degrees of success and deep feat trees' },
  { id: 'starfinder', name: 'Starfinder', icon: <Sparkles className="h-8 w-8" />, color: 'text-cyan-400', border: 'border-cyan-500/20 hover:border-cyan-400/50', glow: 'hover:shadow-cyan-500/10', desc: 'Sci-fi d20 system with stamina/HP split, Resolve Points, and starship combat' },
  { id: 'gurps', name: 'GURPS', icon: <Dices className="h-8 w-8" />, color: 'text-green-400', border: 'border-green-500/20 hover:border-green-400/50', glow: 'hover:shadow-green-500/10', desc: 'Universal 3d6 roll-under — no classes, point-buy everything, realistic and lethal' },
  { id: 'freeform', name: 'Freeform', icon: <Feather className="h-8 w-8" />, color: 'text-purple-400', border: 'border-purple-500/20 hover:border-purple-400/50', glow: 'hover:shadow-purple-500/10', desc: 'Pure storytelling — no dice, no mechanics, just you and the narrative' },
  { id: 'ai_generated', name: 'AI-Generated', icon: <Bot className="h-8 w-8" />, color: 'text-emerald-400', border: 'border-emerald-500/20 hover:border-emerald-400/50', glow: 'hover:shadow-emerald-500/10', desc: 'The AI crafts a unique ruleset tailored to your universe and character' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const { setUser } = useAuth();
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  const handleGetStarted = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/admin-login`,
        { email: adminEmail, password: adminPassword },
        { withCredentials: true }
      );
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      toast.success('Admin login successful');
      setUser(response.data);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      toast.error(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black relative scanline-overlay">
      <MatrixRain opacity={0.25} speed={1.1} />

      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden z-10">
        {/* Darker gradient for contrast with bolder rain */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />

        {/* Language Selector */}
        <div className="absolute top-12 right-6 z-50">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="bg-black/90 border border-blue-500/30 px-4 py-2 rounded-sm text-sm text-blue-300 cursor-pointer outline-none focus:ring-1 focus:ring-blue-500/50 font-mono tracking-wider"
            data-testid="language-selector"
          >
            <option value="en">EN</option>
            <option value="zh">CN</option>
            <option value="ru">RU</option>
            <option value="de">DE</option>
          </select>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2 }}
          >
            {/* Decorative line */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.5, delay: 0.3 }}
              className="w-32 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto mb-8"
            />

            <h1 className="text-7xl md:text-9xl font-bold glow-text mb-4 font-mono tracking-[0.2em] techno-text">
              {t('landing.hero_title')}
            </h1>
            
            <p className="text-base md:text-lg techno-subtitle mb-6">
              {t('landing.hero_subtitle')}
            </p>
            
            <p className="text-sm md:text-base text-blue-200/40 mb-14 max-w-2xl mx-auto leading-relaxed font-mono tracking-wide">
              {t('landing.hero_description')}
            </p>

            <Button
              onClick={handleGetStarted}
              size="lg"
              className="bg-blue-500/90 hover:bg-blue-400 text-black font-bold uppercase tracking-[0.3em] px-14 py-7 text-base rounded-none shadow-[0_0_40px_rgba(96,165,250,0.5)] hover:shadow-[0_0_60px_rgba(96,165,250,0.8)] transition-all hover:scale-105 font-mono border border-blue-400/80"
              data-testid="get-started-btn"
            >
              <Sparkles className="mr-3 h-5 w-5" />
              {t('landing.cta_start')}
            </Button>

            {/* Decorative line */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.5, delay: 0.6 }}
              className="w-32 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto mt-14"
            />
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
        >
          <div className="w-6 h-10 border-2 border-blue-500/40 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-blue-400 rounded-full mt-2" />
          </div>
        </motion.div>
      </div>

      {/* Features Section */}
      <div className="relative py-32 px-6 z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-[11px] font-mono tracking-[0.5em] text-blue-500/50 uppercase mb-3">System.features</p>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-400 font-mono techno-text">
              // Features
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Wand2 className="h-12 w-12" />,
                title: t('landing.feature_1_title'),
                description: t('landing.feature_1_desc')
              },
              {
                icon: <Users className="h-12 w-12" />,
                title: t('landing.feature_2_title'),
                description: t('landing.feature_2_desc')
              },
              {
                icon: <Globe className="h-12 w-12" />,
                title: t('landing.feature_3_title'),
                description: t('landing.feature_3_desc')
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="glass-card p-8 rounded-none border-blue-500/20 hover:border-blue-400/50 transition-all duration-300 group matrix-glow"
                data-testid={`feature-card-${index}`}
              >
                <div className="text-blue-400 mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-blue-300 mb-3 font-mono tracking-wider">
                  {feature.title}
                </h3>
                <p className="text-blue-200/40 leading-relaxed text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Rulesets Section */}
      <div className="relative py-28 px-6 z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <p className="text-[11px] font-mono tracking-[0.5em] text-blue-500/50 uppercase mb-3">System.rulesets</p>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-400 font-mono techno-text mb-3">
              <BookOpen className="inline mr-3 h-7 w-7" />
              6 Rule Systems
            </h2>
            <p className="text-blue-200/30 text-sm font-mono tracking-wider">
              Choose your mechanics. Lock it in. The AI enforces the rules.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4">
            {RULESETS.map((ruleset, index) => (
              <motion.div
                key={ruleset.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`glass-card rounded-none p-5 border ${ruleset.border} transition-all duration-300 group hover:shadow-lg ${ruleset.glow}`}
                data-testid={`ruleset-card-${ruleset.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`${ruleset.color} flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform`}>
                    {ruleset.icon}
                  </div>
                  <div>
                    <h3 className={`text-base font-bold ${ruleset.color} font-mono tracking-wider mb-1`}>
                      {ruleset.name}
                    </h3>
                    <p className="text-blue-200/35 text-sm leading-relaxed">
                      {ruleset.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
            {/* Center the 5th card */}
            <div className="hidden md:block" />
          </div>
        </div>
      </div>

      {/* Universes Section */}
      <div className="relative py-24 px-6 z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[11px] font-mono tracking-[0.5em] text-blue-500/50 uppercase mb-3">System.universes</p>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-400 mb-3 font-mono techno-text">
              <Globe className="inline mr-3 h-7 w-7" />
              4+ Universes to Explore
            </h2>
            <p className="text-blue-200/30 text-sm font-mono tracking-wider">
              Each with strict lore accuracy and timeline adherence
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: 'Star Wars', image: 'https://customer-assets.emergentagent.com/job_41857d1d-1d1e-47df-90b3-d2aa6c5d2539/artifacts/ywfgiehf_image.png', pos: 'top', description: 'Galactic saga' },
              { name: 'Game of Thrones', image: 'https://static.prod-images.emergentagent.com/jobs/41857d1d-1d1e-47df-90b3-d2aa6c5d2539/images/832dd0d28f5042d868cd4082e27fe3ee187ee0fd23fae8beb018db4656c0c2a3.png', pos: '50% 30%', description: 'Westeros politics' },
              { name: 'Lord of the Rings', image: 'https://static.prod-images.emergentagent.com/jobs/41857d1d-1d1e-47df-90b3-d2aa6c5d2539/images/b0ea9e37a8fd385713e663486a95ff78dd6307b918cc294ea81ce8c0b9927a14.png', pos: 'center', description: 'Middle-earth' },
            ].map((universe, index) => (
              <motion.div
                key={universe.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card rounded-none border-blue-500/10 hover:border-blue-400/40 transition-all text-center group cursor-pointer overflow-hidden"
              >
                {universe.image ? (
                  <div className="w-full h-40 overflow-hidden">
                    <img src={universe.image} alt={universe.name} style={{ objectPosition: universe.pos || 'center' }} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  </div>
                ) : (
                  <div className="text-4xl pt-6 pb-3 group-hover:scale-110 transition-transform">
                    {universe.icon}
                  </div>
                )}
                <div className="p-4">
                  <h3 className="text-base font-bold text-blue-300 mb-2 font-mono tracking-wider">
                    {universe.name}
                  </h3>
                  <p className="text-blue-200/30 text-sm">
                    {universe.description}
                  </p>
                </div>
              </motion.div>
            ))}
            {/* Warhammer 40K centered */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="glass-card rounded-none border-blue-500/10 hover:border-blue-400/40 transition-all text-center group cursor-pointer overflow-hidden md:col-start-2"
            >
              <div className="w-full h-40 overflow-hidden">
                <img src="https://static.prod-images.emergentagent.com/jobs/41857d1d-1d1e-47df-90b3-d2aa6c5d2539/images/146e5a6bfde7411e9b0270d1a1731dc28e9ee069c21e3d38fa67ecdd8eada52b.png" alt="Warhammer 40K" style={{ objectPosition: 'top' }} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
              </div>
              <div className="p-4">
                <h3 className="text-base font-bold text-blue-300 mb-2 font-mono tracking-wider">Warhammer 40K</h3>
                <p className="text-blue-200/30 text-sm">Grimdark future</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Chapters System Section */}
      <div className="relative py-28 px-6 z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <p className="text-[11px] font-mono tracking-[0.5em] text-purple-500/50 uppercase mb-3">System.chapters</p>
            <h2 className="text-3xl md:text-4xl font-bold text-purple-400 font-mono techno-text mb-3">
              <Layers className="inline mr-3 h-7 w-7" />
              The Chapter System
            </h2>
            <p className="text-purple-200/30 text-sm font-mono tracking-wider max-w-xl mx-auto">
              Your story never truly ends. Pick up where you left off — or begin a new arc.
            </p>
          </motion.div>

          {/* Main description card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-none border-purple-500/20 p-8 mb-6"
            data-testid="chapters-description-card"
          >
            <div className="flex flex-col md:flex-row items-start gap-8">
              {/* Left: visual flow */}
              <div className="flex-shrink-0 flex flex-col items-center gap-2 pt-2">
                <div className="w-14 h-14 rounded-none border border-purple-500/30 bg-purple-500/5 flex items-center justify-center text-purple-400">
                  <BookOpen className="h-6 w-6" />
                </div>
                <div className="w-px h-8 bg-purple-500/20" />
                <ArrowRight className="h-4 w-4 text-purple-500/40 rotate-90" />
                <div className="w-px h-8 bg-purple-500/20" />
                <div className="w-14 h-14 rounded-none border border-purple-500/30 bg-purple-500/5 flex items-center justify-center text-purple-400">
                  <Layers className="h-6 w-6" />
                </div>
              </div>
              {/* Right: text */}
              <div className="flex-1">
                <h3 className="text-lg font-bold text-purple-300 font-mono tracking-wider mb-3">
                  One Character. Many Chapters.
                </h3>
                <p className="text-blue-200/35 text-sm leading-relaxed mb-4">
                  Every great saga has arcs. When your campaign grows long and the AI starts losing the thread, start a <strong className="text-purple-400">new chapter</strong>. Your character, their inventory, their allies, and their most important memories carry forward — but the AI gets a fresh context window, keeping every response sharp and lore-accurate.
                </p>
                <p className="text-blue-200/35 text-sm leading-relaxed">
                  Each new chapter opens with an AI-generated <strong className="text-purple-400">"Previously on..."</strong> recap — a dramatic narrator summary of everything that happened before, so both you and the AI remember what matters.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Carry-over feature cards */}
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                icon: <Bookmark className="h-6 w-6" />,
                title: 'Memories Persist',
                desc: 'Key moments you bookmarked carry into every new chapter. The AI never forgets what matters to your story.',
                color: 'text-amber-400',
                border: 'border-amber-500/20 hover:border-amber-400/40',
              },
              {
                icon: <Users className="h-6 w-6" />,
                title: 'NPCs Travel With You',
                desc: 'Allies, rivals, and enemies carry forward. Toggle them on or off to control who the AI remembers.',
                color: 'text-emerald-400',
                border: 'border-emerald-500/20 hover:border-emerald-400/40',
              },
              {
                icon: <Eye className="h-6 w-6" />,
                title: 'You Control the Context',
                desc: 'Activate or deactivate any NPC card. Only active characters are injected into the AI\'s memory — you decide who matters.',
                color: 'text-cyan-400',
                border: 'border-cyan-500/20 hover:border-cyan-400/40',
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className={`glass-card rounded-none p-5 border ${feature.border} transition-all duration-300 group`}
                data-testid={`chapter-feature-${index}`}
              >
                <div className={`${feature.color} mb-3 group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h4 className={`text-sm font-bold ${feature.color} font-mono tracking-wider mb-2`}>
                  {feature.title}
                </h4>
                <p className="text-blue-200/30 text-sm leading-relaxed">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-24 px-6 z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-12 rounded-none magic-border"
          >
            <h2 className="text-4xl md:text-5xl font-bold glow-text mb-6 font-mono techno-text">
              Ready to Jack In?
            </h2>
            <p className="text-base text-blue-300/40 mb-8 font-mono tracking-wider">
              Join thousands of adventurers in AI-powered quests
            </p>
            <Button
              onClick={handleGetStarted}
              size="lg"
              className="bg-blue-500/90 hover:bg-blue-400 text-black font-bold uppercase tracking-[0.3em] px-14 py-7 text-base rounded-none shadow-[0_0_40px_rgba(96,165,250,0.5)] hover:shadow-[0_0_60px_rgba(96,165,250,0.8)] transition-all hover:scale-105 font-mono border border-blue-400/80"
              data-testid="cta-bottom-btn"
            >
              <Sparkles className="mr-3 h-5 w-5" />
              {t('landing.cta_start')}
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative py-8 px-6 border-t border-blue-500/10 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-blue-500/20 text-xs font-mono tracking-[0.3em]">
          <p>&copy; 2026 TALEMATRIX // POWERED BY AI</p>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowAdminLogin(!showAdminLogin)}
              className="text-blue-500/15 hover:text-blue-500/40 transition-colors cursor-pointer"
              data-testid="admin-login-toggle"
            >
              <KeyRound className="h-3.5 w-3.5" />
            </button>
            <a href="https://x.com/TaleMatrix" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-500/30 hover:text-blue-400 transition-colors" data-testid="twitter-link">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              <span>@TALEMATRIX</span>
            </a>
          </div>
        </div>
        
        {/* Admin Direct Login — hidden by default */}
        {showAdminLogin && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="max-w-sm mx-auto mt-6"
          >
            <form onSubmit={handleAdminLogin} className="glass-card border border-blue-500/10 p-4 rounded-none space-y-3">
              <p className="text-[10px] font-mono tracking-[0.3em] text-blue-500/30 uppercase text-center">Admin Direct Login</p>
              <input
                type="email"
                placeholder="Admin email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full bg-black/60 border border-blue-500/20 text-blue-200 text-xs font-mono px-3 py-2 rounded-none outline-none focus:border-blue-400/50"
                data-testid="admin-email-input"
              />
              <input
                type="password"
                placeholder="Password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="w-full bg-black/60 border border-blue-500/20 text-blue-200 text-xs font-mono px-3 py-2 rounded-none outline-none focus:border-blue-400/50"
                data-testid="admin-password-input"
              />
              <Button
                type="submit"
                disabled={adminLoading || !adminEmail || !adminPassword}
                className="w-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 text-xs font-mono tracking-wider rounded-none"
                data-testid="admin-login-btn"
              >
                {adminLoading ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : null}
                {adminLoading ? 'Authenticating...' : 'Login'}
              </Button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}
